import React, { useEffect, useMemo } from 'react';
import { useUploadJobs } from '../../context/UploadJobsContext';

interface ValidationJobsPanelProps {
  refreshToken: number;
}

const STATUS_LABELS: Record<string, string> = {
  queued: 'Queued',
  uploading: 'Uploading',
  preparing: 'Preparing',
  ready: 'Ready',
  verifying: 'Verifying',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
  acknowledged: 'Acknowledged',
};

const ValidationJobsPanel: React.FC<ValidationJobsPanelProps> = ({ refreshToken }) => {
  const { validationJobs, refreshNotifications, isPolling } = useUploadJobs();

  useEffect(() => {
    refreshNotifications();
  }, [refreshNotifications, refreshToken]);

  const latestJob = useMemo(() => validationJobs[0], [validationJobs]);

  const openBell = () => {
    window.dispatchEvent(new CustomEvent('upload-bell:open'));
  };

  return (
    <div className="validation-jobs-panel">
      <div className="panel-header">
        <div>
          <h2>Email Validation Jobs</h2>
          <p className="panel-subtitle">Monitor queued and completed Bouncify runs</p>
        </div>
        <button
          className="wizrdom-button ghost"
          type="button"
          onClick={refreshNotifications}
          disabled={isPolling}
        >
          {isPolling ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="validation-notice-card">
        <div>
          <strong>Heads up:</strong> email validation alerts now live in the notification bell.
          <p className="validation-notice-copy">
            Open the bell to follow every job, dismiss alerts, and review completed runs without leaving the dashboard.
          </p>
        </div>
        <button type="button" className="wizrdom-button" onClick={openBell}>
          View notifications
        </button>
      </div>

      {validationJobs.length === 0 && (
        <div className="empty-jobs-state">
          {isPolling ? 'Loading validation jobs…' : 'No validation jobs yet. Launch one to see live updates.'}
        </div>
      )}

      {latestJob && (
        <div className="jobs-summary">
          {(() => {
            const latestStatusKey = latestJob.status?.toLowerCase() ?? 'queued';
            const latestStatusLabel = STATUS_LABELS[latestStatusKey] ?? latestJob.status ?? 'Unknown';
            return (
              <div>
                <strong>Latest Job:</strong> {latestStatusLabel}
              </div>
            );
          })()}
          <div>
            <strong>Deliverable:</strong> {latestJob.deliverable_count ?? 0}
          </div>
          <div>
            <strong>Undeliverable:</strong> {latestJob.undeliverable_count ?? 0}
          </div>
          <div>
            <strong>Accept-All:</strong> {latestJob.accept_all_count ?? 0}
          </div>
          <div>
            <strong>Unknown:</strong> {latestJob.unknown_count ?? 0}
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidationJobsPanel;
